import React, {useCallback, useState} from 'react';
import {useTranslation} from "react-i18next";
import {
    type LocalDocumentContentResponse,
    LocalDocumentNavigationData
} from "../../../../../models/local-documents.models";

import {Box, Button, Divider, IconButton, Tooltip} from "@mui/material";
import DigitizedDocumentFileInfo from "../../../../../components/documents-digitization/DigitizedDocumentFileInfo";
import {OptionsButton} from "../../../../../components/data-sources/DataSourceTable.styled";
import SecondaryActionButton from "../../../../../components/documents-digitization/SecondaryActionButton";
import ContextMenu from "../../../../../components/menu/ContextMenu";
import CompactNavigator from "../CompactNavigator";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ElectricBoltOutlined from '@mui/icons-material/ElectricBoltOutlined';
import {ReactComponent as MoreIcon} from "../../../../../assets/icons/more.svg";
import {ReactComponent as EditIcon} from "../../../../../assets/icons/edit.svg";
import {ReactComponent as TrashIcon} from "../../../../../assets/icons/trash.svg";

import {
    DigitizationToolbarWrapper,
    StyledHeaderActions,
    StyledToolbarContainer,
    StyledToolbarHeader,
} from "./DigitizationDetailsToolbar.styled";

interface DigitizationDetailsToolbarProps {
    document: LocalDocumentContentResponse | null;
    navigation: LocalDocumentNavigationData | null;
    onCancelDocument: () => void;
    onConfirmDocument: () => void;
    navigateToPreviousDocument: () => void;
    navigateToNextDocument: () => void;
    navigateBack: () => void;
    navigateToEdition: () => void;
    onExport: () => Promise<void>;
}

const DigitizationDetailsToolbar = ({
    document,
    navigation,
    onConfirmDocument,
    onCancelDocument,
    navigateToPreviousDocument,
    navigateToNextDocument,
    navigateBack,
    navigateToEdition,
    onExport,
}: DigitizationDetailsToolbarProps) => {
    const {t} = useTranslation();

    const [contextMenu, setContextMenu] = useState<{
        mouseX?: number;
        mouseY?: number;
        anchorEl?: HTMLElement | null;
        document: LocalDocumentContentResponse;
    } | null>(null);

    const handleContextMenu = useCallback((event: React.MouseEvent, document: LocalDocumentContentResponse) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            anchorEl: event.currentTarget as HTMLElement,
            document,
        });
    }, []);

    const handleConfirmDocument = () => {
        if (!document || document.approvalStatus === "APPROVED") return;
        onConfirmDocument();
    };

    const handleNavigateToAiAssistant = () => {
        // console.log('Navigate to AI assistant');
    };

    return (
        <DigitizationToolbarWrapper>
            <StyledToolbarContainer>
                <StyledToolbarHeader>
                    <Tooltip title={t('common.back')}>
                        <IconButton color="primary"
                                    aria-label={t('common.back') as string}
                                    onClick={navigateBack}
                        >
                            <ChevronLeftIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                    </Tooltip>
                    <Divider orientation="vertical" flexItem />

                    {navigation && document && (
                        <>
                            <CompactNavigator
                                currentIndex={navigation.position}
                                total={navigation.totalElements}
                                hasPrevious={navigation.hasPrevious}
                                hasNext={navigation.hasNext}
                                onPrevious={navigateToPreviousDocument}
                                onNext={navigateToNextDocument}
                            />
                            <Divider orientation="vertical" flexItem />

                            <DigitizedDocumentFileInfo isStatusVisible={true} document={document}/>
                        </>
                    )}
                </StyledToolbarHeader>

                {navigation && document && (
                    <StyledHeaderActions>
                        <SecondaryActionButton
                            startIcon={<ElectricBoltOutlined style={{fontSize: '22px'}} />}
                            disabled={true}
                            onClick={handleNavigateToAiAssistant}
                        >
                            {t('digitizationDetails.tools.aiAssistantButton')}
                        </SecondaryActionButton>

                        <SecondaryActionButton
                            startIcon={<CheckCircleOutlineRoundedIcon style={{fontSize: '22px'}} />}
                            onClick={handleConfirmDocument}
                            disabled={document.approvalStatus === 'APPROVED' || document.status === 'ARCHIVED'}
                        >
                            {t('digitizationDetails.tools.confirmButton')}
                        </SecondaryActionButton>

                        <Button variant="contained" color="primary" onClick={onExport} disabled={true}>
                            {t('digitizationDetails.tools.exportButton')}
                        </Button>

                        <SecondaryActionButton
                            startIcon={<EditIcon width="22" height="22" />}
                            onClick={navigateToEdition}
                        >
                            {t('digitizationDetails.tools.editButton')}
                        </SecondaryActionButton>

                        <Box sx={{width: "20px"}}>
                            <OptionsButton
                                className="desktop-only"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleContextMenu(e, document);
                                }}
                                aria-haspopup="listbox"
                                disabled={true}
                            >
                                <MoreIcon aria-hidden={true} focusable={false}/>
                            </OptionsButton>
                        </Box>
                    </StyledHeaderActions>
                )}
            </StyledToolbarContainer>

            {contextMenu && (
                <ContextMenu
                    id={contextMenu ? `digitized-document-menu-${contextMenu.document.id}` : undefined}
                    open={true}
                    anchorEl={contextMenu.anchorEl}
                    mouseX={contextMenu.mouseX}
                    mouseY={contextMenu.mouseY}
                    onClose={() => setContextMenu(null)}
                    actions={[
                        {
                            key: 'cancel-document',
                            label: t('digitizationDetails.tools.cancelButton') as string,
                            icon: <TrashIcon style={{width: 16, height: 16}} />,
                            onClick: onCancelDocument,
                            color: '#f44336',
                        },
                    ]}
                />)
            }
        </DigitizationToolbarWrapper>
    );
};

export default DigitizationDetailsToolbar;